/**
 * Blog Infinite Scroll Script
 * Handles infinite scroll loading for paginated articles
 * Supports both regular list and year list
 */

// 중복 선언 방지
if (typeof window.BlogPageLoader === 'undefined') {
  window.BlogPageLoader = class BlogPageLoader {
    constructor(options = {}) {
      this.mode = options.mode || 'regular'; // 'regular' or 'year'
      this.list = options.mode === 'year'
        ? document.querySelector('#article-year-list')
        : document.querySelector('#article-list');
      this.trigger = document.querySelector('#load-more');
      this.totalPages = this.trigger?.dataset.totalPages;
      this.loadingWrapper = document.querySelector('#article-loading');
      this.isLoading = false;
      this.isInitialized = false;
      this.observer = null;

      this.init();
    }

    init() {
      // 초기 아이템 visible 처리 (항상 실행)
      this.makeInitialItemsVisible();

      // list가 없으면 종료
      if (!this.list) {
        return;
      }

      // trigger가 없으면 페이지네이션이 필요 없는 경우이므로 종료
      if (!this.trigger) {
        return;
      }

      // 이미 초기화되었는지 확인
      if (this.isInitialized) {
        return;
      }

      this.setupScrollObserver();
      this.isInitialized = true;
    }

    // 초기 아이템들을 visible로 만드는 독립 메서드
    makeInitialItemsVisible() {
      const closedArticleItems = document.querySelectorAll('.closed-article-item');

      if (closedArticleItems.length !== 0) {
        closedArticleItems.forEach(item => {
          item.classList.add('visible');
        });
      }
    }

    setupScrollObserver() {
      this.observer = new IntersectionObserver(
        async ([entry]) => {
          if (!entry.isIntersecting || this.isLoading) return;

          const nextPage = this.trigger?.dataset.nextPage;
          if (!nextPage) return;

          await this.loadNextPage(nextPage);
        },
        {
          rootMargin: '800px',
        }
      );

      this.observer.observe(this.trigger);
    }

    async loadNextPage(nextPage) {
      // 이미 로딩 중이면 중단
      if (this.isLoading) return;

      this.isLoading = true;
      this.showLoading();

      if(this.totalPages < nextPage) {
        this.cleanup();
        this.isLoading = false;
        this.hideLoading();
        if (this.trigger && this.trigger.parentNode) {
          this.trigger.remove();
        }
        return;
      }

      try {
        const url = `${location.pathname}?page=${nextPage}`;
        const res = await fetch(url);

        if (!res.ok) {
          throw new Error(`${res.status}`);
        }

        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        // 모드에 따라 다르게 처리
        if (this.mode === 'year') {
          this.handleYearGroupedItems(doc);
        } else {
          this.handleRegularItems(doc);
        }

        // 다음 페이지 확인
        const nextTrigger = doc.querySelector('#load-more');
        if (nextTrigger && nextTrigger.dataset.nextPage) {
          if(this.totalPages < nextTrigger.dataset.nextPage) {
            this.cleanup();
            this.isLoading = false;
            this.hideLoading();
            if (this.trigger && this.trigger.parentNode) {
              this.trigger.remove();
            }
            return;
          } else {
            this.trigger.dataset.nextPage = nextTrigger.dataset.nextPage;
          }
        }
      } catch (error) {
        console.error('[BlogPageLoader] 로드 실패:', error);
      } finally {
        this.isLoading = false;
        this.hideLoading();
      }
    }

    // 일반 목록 처리
    handleRegularItems(doc) {
      const newItems = doc.querySelectorAll('#article-list .article-item');

      newItems.forEach((item, index) => {
        this.list.appendChild(item);
        // 애니메이션을 위한 지연 추가
        setTimeout(() => {
          item.classList.add('visible');
        }, 100 + (index * 50));
      });
    }

    // 연도별 그룹 목록 처리
    handleYearGroupedItems(doc) {
      const newItems = doc.querySelectorAll('.closed-article-item');

      newItems.forEach((item, index) => {
        // 아이템에서 연도 정보 추출
        const year = this.extractYearFromArticle(item);

        if (year) {
          // 해당 연도 그룹 찾기
          let yearGroup = this.list.querySelector(`.year-group[data-year="${year}"]`);

          // 연도 그룹이 없으면 생성
          if (!yearGroup) {
            yearGroup = this.createYearGroup(year);
            this.insertYearGroupInOrder(yearGroup, year);
          }

          // 해당 연도 그룹의 articles 컨테이너에 추가
          const articlesContainer = yearGroup.querySelector('.closed-articles');
          if (articlesContainer) {
            articlesContainer.appendChild(item);

            // 애니메이션을 위한 지연 추가
            setTimeout(() => {
              item.classList.add('visible');
            }, 100 + (index * 50));
          }
        }
      });
    }

    // 아이템에서 연도 추출
    extractYearFromArticle(item) {
      const yearAttr = item.dataset.year;
      if (yearAttr) return yearAttr;

      return null;
    }

    // 연도 그룹 생성
    createYearGroup(year) {
      const yearGroup = document.createElement('div');
      yearGroup.className = 'closed-year-group';
      yearGroup.dataset.year = year;

      yearGroup.innerHTML = `
        <h3 class="fs-en-c2 pb-sm text-text-tertiary">${year}</h3>
        <div class="flex flex-col closed-articles"></div>
      `;

      return yearGroup;
    }

    // 연도 그룹을 정렬된 위치에 삽입 (최신 연도가 위로)
    insertYearGroupInOrder(newGroup, newYear) {
      const existingGroups = this.list.querySelectorAll('.year-group');
      let inserted = false;

      // 로딩 UI 요소 찾기
      const loadingUI = this.list.querySelector('#article-loading');

      for (const group of existingGroups) {
        const existingYear = group.dataset.year;
        if (newYear > existingYear) {
          this.list.insertBefore(newGroup, group);
          inserted = true;
          break;
        }
      }

      // 가장 오래된 연도이거나 그룹이 없는 경우
      if (!inserted) {
        if (loadingUI) {
          this.list.insertBefore(newGroup, loadingUI);
        } else {
          this.list.appendChild(newGroup);
        }
      }
    }

    showLoading() {
      if (this.loadingWrapper) {
        this.loadingWrapper.classList.add('active');
      }
    }

    hideLoading() {
      if (this.loadingWrapper) {
        this.loadingWrapper.classList.remove('active');
      }
    }

    cleanup() {
      if (this.observer) {
        this.observer.disconnect();
      }
    }
  };
}

// 초기화 함수
function initializeBlogPageLoader() {
  // 이미 초기화되었는지 확인
  if (window.blogPageLoaderInstance) return;

  // 1. 연도별 그룹 구조 확인
  const yearList = document.querySelector('#article-year-list');
  if (yearList) {
    // trigger가 없어도 초기화 (visible 처리를 위해)
    window.blogPageLoaderInstance = new window.BlogPageLoader({ mode: 'year' });
    return;
  }

  // 2. 일반 목록 구조 확인
  const regularList = document.querySelector('#article-list');
  if (regularList) {
    // trigger가 없어도 초기화 (visible 처리를 위해)
    window.blogPageLoaderInstance = new window.BlogPageLoader({ mode: 'regular' });
  }
}

// DOM이 로드되면 초기화 (중복 실행 방지)
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeBlogPageLoader);
} else {
  initializeBlogPageLoader();
}